import {
  PreScreeningQuestion,
  QuestionType,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  CARDIOVASCULAR_ATTRIBUTES,
  DIABETES_ATTRIBUTES,
  MEASUREMENT_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  HORMONE_ATTRIBUTES,
  CANCER_ATTRIBUTES,
  GASTROINTESTINAL_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  IMMUNOSUPPRESSION_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for Amgen CVOT Study (VCVDAMG196)
 *
 * Internal name: Amgen CVOT
 * Target population: Adults 45+ with BMI ≥27, established ASCVD, and excess weight
 *
 * Key inclusion criteria:
 * - Age ≥45
 * - BMI ≥27
 * - History of ASCVD (heart attack, stroke, or peripheral artery disease)
 *
 * Key exclusion criteria:
 * - Heart failure class IV
 * - Type 1 diabetes
 * - Type 2 diabetes with HbA1c ≥11%
 * - Pancreatitis (chronic or acute in past 6 months)
 * - MEN2 or medullary thyroid carcinoma (personal or family history)
 * - Liver failure or cirrhosis
 * - Active cancer (past 5 years with exceptions)
 * - Organ transplant or on transplant waitlist
 * - End-stage kidney disease or dialysis
 * - Pregnant or breastfeeding
 * - GLP-1 use in past 3 months
 * - Clinical trial participation in past 3 months
 */
export const amgenCVOTScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "age_eligibility",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      { value: "45_or_older", label: "I am 45 years of age or older" },
      {
        value: "under_45",
        label: "I am under 45 years of age",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study is for adults who are 45 years of age or older.",
    attributeMapping: mappers.value({
      "45_or_older": [],
      under_45: [],
    }),
  },

  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT,
    }),
  },

  {
    ...standardWeightQuestion,
    minBMI: 27,
    attributeMapping: mappers.weight({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT,
    }),
  },

  {
    id: "cardiovascular_history",
    type: QuestionType.CHECKBOX,
    question: "Have you been diagnosed with any of the following cardiovascular conditions?",
    helpText: "Select all that apply. You must have at least one of these conditions to qualify for this study.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "previous_heart_attack",
        label: "I have had a heart attack confirmed by a doctor",
      },
      {
        value: "previous_stroke",
        label: "I have had a stroke or transient ischemic attack (TIA) confirmed by a doctor",
      },
      {
        value: "peripheral_artery_disease",
        label: "I have peripheral artery disease (PAD) confirmed by a doctor",
      },
      {
        value: "none_of_above",
        label: "None of the above",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study is for people with a history of atherosclerotic cardiovascular disease (heart attack, stroke, or peripheral artery disease).",
    attributeMapping: mappers.value({
      previous_heart_attack: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: true },
      ],
      previous_stroke: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: true },
      ],
      peripheral_artery_disease: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PERIPHERAL_ARTERY_DISEASE, booleanAttributeValue: true },
      ],
      none_of_above: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: false },
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: false },
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PERIPHERAL_ARTERY_DISEASE, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "diabetes_status",
    type: QuestionType.RADIO,
    question: "Do you have diabetes?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "type1_diabetes",
        label: "Yes, I have type 1 diabetes",
        disqualify: true,
      },
      {
        value: "type2_diabetes",
        label: "Yes, I have type 2 diabetes",
      },
      {
        value: "no_diabetes",
        label: "No, I don't have diabetes",
      },
    ],
    disqualifyMessage: "This study cannot include people with type 1 diabetes.",
    attributeMapping: mappers.value({
      type1_diabetes: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: true },
      ],
      type2_diabetes: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: true },
      ],
      no_diabetes: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: false },
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "hba1c_status",
    type: QuestionType.RADIO,
    question: "If you have type 2 diabetes, what is your most recent HbA1c level?",
    helpText: "HbA1c is a measure of your average blood sugar over the past 2-3 months. If you don't have type 2 diabetes or don't know your HbA1c, select 'Not applicable or don't know'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "not_applicable",
        label: "I don't have type 2 diabetes or I don't know my HbA1c",
      },
      {
        value: "hba1c_under_11",
        label: "My HbA1c is less than 11%",
      },
      {
        value: "hba1c_11_or_higher",
        label: "My HbA1c is 11% or higher",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study requires an HbA1c level below 11% for participants with type 2 diabetes.",
    attributeMapping: mappers.value({
      not_applicable: [],
      hba1c_under_11: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HBA1C_MORE_THAN_10, booleanAttributeValue: false },
      ],
      hba1c_11_or_higher: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HBA1C_MORE_THAN_10, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "cardiovascular_exclusions",
    type: QuestionType.CHECKBOX,
    question: "Do any of the following apply to you?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "severe_heart_failure",
        label: "I have severe heart failure (Class IV - unable to carry out any physical activity without discomfort)",
        disqualify: true,
      },
      {
        value: "none",
        label: "None of the above",
      },
    ],
    disqualifyMessage: "This study cannot include participants with severe heart failure (Class IV).",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        severe_heart_failure: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: true },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "pancreatic_thyroid_conditions",
    type: QuestionType.CHECKBOX,
    question: "Do any of the following apply to you or your immediate family?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "chronic_pancreatitis",
        label: "I have chronic pancreatitis (ongoing inflammation of the pancreas)",
        disqualify: true,
      },
      {
        value: "acute_pancreatitis_6_months",
        label: "I have had acute pancreatitis in the past 6 months",
        disqualify: true,
      },
      {
        value: "mtc_men2_history",
        label: "I have or a first-degree relative (parent, sibling, or child) has medullary thyroid carcinoma (MTC) or multiple endocrine neoplasia type 2 (MEN2)",
        disqualify: true,
      },
      {
        value: "none",
        label: "None of the above",
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants with these conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        chronic_pancreatitis: [
          { attributeDefinitionId: GASTROINTESTINAL_ATTRIBUTES.HAS_PANCREATITIS, booleanAttributeValue: true },
        ],
        acute_pancreatitis_6_months: [
          { attributeDefinitionId: GASTROINTESTINAL_ATTRIBUTES.HAS_PANCREATITIS, booleanAttributeValue: true },
        ],
        mtc_men2_history: [],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: GASTROINTESTINAL_ATTRIBUTES.HAS_PANCREATITIS, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "serious_medical_conditions",
    type: QuestionType.CHECKBOX,
    question: "Do any of the following serious medical conditions apply to you?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "liver_failure_cirrhosis",
        label: "I have liver failure or cirrhosis",
        disqualify: true,
      },
      {
        value: "kidney_failure_dialysis",
        label: "I have end-stage kidney disease or I am on dialysis",
        disqualify: true,
      },
      {
        value: "cancer_5_years",
        label: "I have been diagnosed with or treated for cancer in the past 5 years (excluding non-melanoma skin cancers, cervical carcinoma in-situ, stable prostate cancer, or resolved/in-situ breast cancer)",
        disqualify: true,
      },
      {
        value: "organ_transplant",
        label: "I have had an organ transplant or I am on the transplant waitlist",
        disqualify: true,
      },
      {
        value: "pregnant_breastfeeding",
        label: "I am currently pregnant or breastfeeding",
        disqualify: true,
      },
      {
        value: "none",
        label: "None of the above",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these serious medical conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        liver_failure_cirrhosis: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: true },
        ],
        kidney_failure_dialysis: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: true },
        ],
        cancer_5_years: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: true },
        ],
        organ_transplant: [
          { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: true },
        ],
        pregnant_breastfeeding: [
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: true },
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_BREASTFEEDING, booleanAttributeValue: true },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: false },
          { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: false },
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: false },
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_BREASTFEEDING, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "glp1_recent_use",
    type: QuestionType.RADIO,
    question: "Have you taken any GLP-1 receptor agonist medications (such as Ozempic, Mounjaro, Wegovy, Victoza, or Trulicity) in the past 3 months?",
    helpText: "GLP-1 receptor agonists are injectable or oral medications often used for diabetes or weight management.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes_past_3_months",
        label: "Yes, I have taken GLP-1 medications in the past 3 months",
        disqualify: true,
      },
      {
        value: "no_or_stopped_earlier",
        label: "No, I have not taken GLP-1 medications in the past 3 months",
      },
    ],
    disqualifyMessage: "This study cannot include participants who have taken GLP-1 medications in the past 3 months.",
    attributeMapping: mappers.value({
      yes_past_3_months: [
        {
          attributeDefinitionId: 25710, // GLP1Rs_semaglutides_tirzepatides
          medicationGroupAttributeValue: {
            hasTaken: true,
            intervals: [{ start: {}, end: { isBeforeX: true, year: 2024 } }],
          },
        },
      ],
      no_or_stopped_earlier: [
        {
          attributeDefinitionId: 25710,
          medicationGroupAttributeValue: { hasTaken: false },
        },
      ],
    }),
  },

  {
    id: "clinical_trial_participation",
    type: QuestionType.RADIO,
    question: "Have you participated in any clinical trial in the past 3 months?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes_past_3_months",
        label: "Yes, I have participated in a clinical trial in the past 3 months",
        disqualify: true,
      },
      {
        value: "no_or_longer_ago",
        label: "No, I have not participated in a clinical trial in the past 3 months",
      },
    ],
    disqualifyMessage: "This study requires that you have not participated in another clinical trial in the past 3 months.",
    attributeMapping: mappers.value({
      yes_past_3_months: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
      no_or_longer_ago: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
    }),
  },
];
